import React, { useState, useEffect, useMemo, useRef } from 'react';
import { 
  UserPlus, Mail, Shield, User as UserIcon, X, Check, Loader2, Users, 
  Trash2, Save, User as UserEditIcon, CreditCard, Receipt, Wallet, ArrowUpRight, CheckCircle2,
  Calendar, Download, Zap, Upload, FileSpreadsheet, MapPin, HardDrive, UserCheck, Star, 
  ChevronRight, Building2, ClipboardCheck, QrCode, Trophy, AlertCircle, Database, Server,
  AlertTriangle, Send, GitBranch, Plus, RotateCcw, MessageSquare
} from 'lucide-react';
import { User, UserRole, Container, UserPermissions, SessionLog, Contact, PLAN_LIMITS, DEFAULT_TASK_SECTORS, CustomSector, WhatsAppAssessor } from '../types';
import { Phone, Edit2 } from 'lucide-react';
import { supabase } from '../supabase';
import TeamList from './TeamList';

const AddAssessorForm: React.FC<{ containerId: string; currentUserId: string; onDone?: () => void }> = ({ containerId, currentUserId, onDone }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [adding, setAdding] = useState(false);
  return (
    <div className="mb-4">
      {showAdd ? (
        <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-200 dark:border-emerald-800">
          <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="Nome" className="bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-lg px-3 py-2 text-sm font-bold dark:text-white outline-none flex-1" />
          <input value={addPhone} onChange={e => setAddPhone(e.target.value)} placeholder="5511999999999" className="bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-lg px-3 py-2 text-sm font-bold dark:text-white outline-none w-44" />
          <button disabled={adding || !addName.trim() || !addPhone.trim()} onClick={async () => {
            setAdding(true);
            let phone = addPhone.replace(/\D/g, '');
            if (!phone.startsWith('55')) phone = '55' + phone;
            await supabase.from('whatsapp_assessors').insert({ container_id: containerId, name: addName.trim(), phone, created_by: currentUserId });
            setAddName(''); setAddPhone(''); setShowAdd(false); setAdding(false);
            if (onDone) onDone();
          }} className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-black hover:bg-emerald-700 transition-all disabled:opacity-50">
            {adding ? '...' : 'Salvar'}
          </button>
          <button onClick={() => setShowAdd(false)} className="p-2 text-neutral-400 hover:text-neutral-600"><X size={16}/></button>
        </div>
      ) : (
        <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 px-4 py-2.5 border-2 border-dashed border-emerald-300 dark:border-emerald-700 rounded-xl text-sm font-bold text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-all">
          <Plus size={16} /> Adicionar assessor
        </button>
      )}
    </div>
  );
};

const AssessorRow: React.FC<{ assessor: WhatsAppAssessor; sendingId: string | null; sendResult: { id: string; msg: string } | null; onSendTasks: (id: string) => void; onUpdate?: () => void }> = ({ assessor: a, sendingId, sendResult, onSendTasks, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(a.name);
  const [editPhone, setEditPhone] = useState(a.phone);
  const [saving, setSaving] = useState(false);
  return (
    <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-navy-800 rounded-2xl border border-neutral-100 dark:border-navy-700">
      {editing ? (
        <div className="flex-1 flex items-center gap-3">
          <input value={editName} onChange={e => setEditName(e.target.value)} className="bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-lg px-3 py-2 text-sm font-bold dark:text-white outline-none flex-1" />
          <input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="5511999999999" className="bg-white dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-lg px-3 py-2 text-sm font-bold dark:text-white outline-none w-40" />
          <button disabled={saving} onClick={async () => {
            setSaving(true);
            let phone = editPhone.replace(/\D/g, '');
            if (!phone.startsWith('55')) phone = '55' + phone;
            await supabase.from('whatsapp_assessors').update({ name: editName.trim(), phone }).eq('id', a.id);
            setSaving(false);
            setEditing(false);
            if (onUpdate) onUpdate();
          }} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg"><Save size={16}/></button>
          <button onClick={() => setEditing(false)} className="p-2 text-neutral-400 hover:bg-neutral-100 rounded-lg"><X size={16}/></button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-xl flex items-center justify-center font-black text-sm">{a.name.charAt(0)}</div>
            <div>
              <p className="font-bold text-navy-950 dark:text-white">{a.name}</p>
              <p className="text-xs text-emerald-600 font-mono">{a.phone}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => onSendTasks(a.id)} disabled={sendingId === a.id} className="p-2 text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-all" title="Enviar tarefas via WhatsApp">
              {sendingId === a.id ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />}
            </button>
            {sendResult?.id === a.id && <span className={`text-[10px] font-bold ${sendResult.msg.includes('enviadas') ? 'text-emerald-600' : 'text-amber-600'}`}>{sendResult.msg}</span>}
            <button onClick={() => setEditing(true)} className="p-2 text-neutral-400 hover:text-parliament-600 hover:bg-parliament-50 rounded-lg transition-all"><Edit2 size={14}/></button>
            <button onClick={async () => {
              if (!confirm(`Excluir assessor "${a.name}"?`)) return;
              await supabase.from('whatsapp_assessors').delete().eq('id', a.id);
              if (onUpdate) onUpdate();
            }} className="p-2 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><Trash2 size={14}/></button>
          </div>
        </>
      )}
    </div>
  );
};

interface AdminSettingsProps {
  users: User[];
  container: Container | null;
  currentUser: User;
  contacts: Contact[];
  autoOpenProfile?: boolean;
  onUpdateUsers: () => void;
  onUpdateProfile?: () => void;
  onRefreshContainer?: () => void; 
  sessionLogs: SessionLog[]; 
  customSectors?: CustomSector[];
  onUpdateSectors?: () => void;
  onOpenSupport?: () => void;
  waAssessors?: WhatsAppAssessor[];
  onUpdateAssessors?: () => void;
}

type SubTab = 'team' | 'billing' | 'profile' | 'gamification' | 'workflow';

const AdminSettings: React.FC<AdminSettingsProps> = ({ users, container, currentUser, contacts, autoOpenProfile, onUpdateUsers, onUpdateProfile, onRefreshContainer, sessionLogs, customSectors = [], onUpdateSectors, onOpenSupport, waAssessors = [], onUpdateAssessors }) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>('team');
  const [newName, setNewName] = useState(currentUser.name);
  const [newPhone, setNewPhone] = useState(currentUser.phone || '');
  const [nameLoading, setNameLoading] = useState(false);
  const [nameMsg, setNameMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  
  const [pointsConfig, setPointsConfig] = useState({ contact_points: 10, attendance_points: 5 });
  const [gamificationLoading, setGamificationLoading] = useState(false);
  const [gamificationMsg, setGamificationMsg] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  // States for Billing Modals
  const [manageSubscriptionOpen, setManageSubscriptionOpen] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState<{ open: boolean, type: 'change' | 'cancel', targetPlan?: string, planName?: string } | null>(null);
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);

  // States for assessor task sending
  const [sendingAssessorId, setSendingAssessorId] = useState<string | null>(null);
  const [assessorSendResult, setAssessorSendResult] = useState<{ id: string; msg: string } | null>(null);

  const handleSendAssessorTasks = async (assessorId: string) => {
    if (!container?.id) return;
    setSendingAssessorId(assessorId);
    setAssessorSendResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-notify', {
        body: { event: 'send_all_tasks', container_id: container.id, target_user_id: `wa_${assessorId}` },
      });
      if (error) {
        setAssessorSendResult({ id: assessorId, msg: error.message || 'Erro ao enviar' });
      } else if (data?.error) {
        setAssessorSendResult({ id: assessorId, msg: data.error });
      } else {
        const count = data?.taskCount || 0;
        setAssessorSendResult({ id: assessorId, msg: count > 0 ? `${count} tarefas enviadas!` : 'Sem tarefas pendentes' });
      }
    } catch (err: any) {
      setAssessorSendResult({ id: assessorId, msg: err.message || 'Erro' });
    } finally {
      setSendingAssessorId(null);
      setTimeout(() => setAssessorSendResult(null), 4000);
    }
  };

  // States for Sectors
  const [newSectorName, setNewSectorName] = useState('');
  const [sectorLoading, setSectorLoading] = useState(false);
  const [showDeletedSectors, setShowDeletedSectors] = useState(false);

  const isSuperAdmin = currentUser.role === UserRole.SUPERADMIN;
  const isCabinetAdmin = currentUser.role === UserRole.ADMIN;

  const currentPlan = container?.plan || 'essential';
  
  // Computed Sectors for Display
  const disabledDefaults = container?.disabled_sectors || [];
  const activeCustom = customSectors.filter(s => s.is_active);
  const inactiveCustom = customSectors.filter(s => !s.is_active);

  const plans = [
    // ... (plans config same as before) ...
    { 
      id: 'essential', 
      name: 'Essencial', 
      priceM: `R$ ${PLAN_LIMITS.essential.priceMonthly}`, 
      priceY: `R$ ${PLAN_LIMITS.essential.installmentYearly}`, 
      contactLimit: PLAN_LIMITS.essential.contacts,
      contactLabel: `${(PLAN_LIMITS.essential.contacts/1000)}k Contatos`,
      storageLimitGB: 5,
      storageLabel: '5GB Armazenamento'
    },
    { 
      id: 'advanced', 
      name: 'Avançado', 
      priceM: `R$ ${PLAN_LIMITS.advanced.priceMonthly}`, 
      priceY: `R$ ${PLAN_LIMITS.advanced.installmentYearly}`, 
      contactLimit: PLAN_LIMITS.advanced.contacts,
      contactLabel: `${(PLAN_LIMITS.advanced.contacts/1000)}k Contatos`,
      storageLimitGB: 50,
      storageLabel: '50GB Armazenamento'
    },
    {
      id: 'pro',
      name: 'Pro',
      priceM: `R$ ${PLAN_LIMITS.pro.priceMonthly}`,
      priceY: `R$ ${PLAN_LIMITS.pro.installmentYearly}`,
      contactLimit: 1000000,
      contactLabel: 'Contatos Ilimitados',
      storageLimitGB: 100,
      storageLabel: '100GB Armazenamento'
    },
    ...(currentPlan === 'betatester' ? [{
      id: 'betatester',
      name: 'Beta Tester',
      priceM: 'Grátis',
      priceY: 'Grátis',
      contactLimit: 1000000,
      contactLabel: 'Contatos Ilimitados',
      storageLimitGB: 100,
      storageLabel: '100GB Armazenamento'
    }] : [])
  ];

  const currentPlanDetails = plans.find(p => p.id === currentPlan) || plans[0];

  useEffect(() => {
    if (container?.gamification) {
      setPointsConfig(container.gamification);
    }
  }, [container?.gamification]);

  useEffect(() => {
    if (autoOpenProfile) {
      setActiveSubTab('profile');
    } else if (!isSuperAdmin && !isCabinetAdmin) {
      setActiveSubTab('profile');
    }
  }, [autoOpenProfile, isSuperAdmin, isCabinetAdmin]);

  const handleUpdateName = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameLoading(true);
    setNameMsg(null);
    try {
      let phoneDigits = newPhone.replace(/\D/g, '') || null;
      if (phoneDigits && !phoneDigits.startsWith('55')) phoneDigits = '55' + phoneDigits;
      const { error } = await supabase.from('profiles').update({ name: newName.trim(), phone: phoneDigits }).eq('id', currentUser.id);
      if (error) throw error;
      setNameMsg({ type: 'success', text: 'Nome atualizado com sucesso!' });
      if (onUpdateProfile) onUpdateProfile();
    } catch (err: any) {
      setNameMsg({ type: 'error', text: err.message });
    } finally {
      setNameLoading(false);
    }
  };

  const handleUpdateGamification = async (e: React.FormEvent) => {
    e.preventDefault();
    if(!container) return;

    setGamificationLoading(true);
    setGamificationMsg(null);
    try {
      const { error } = await supabase.from('containers').update({ gamification: pointsConfig }).eq('id', container.id);
      if(error) throw error;
      setGamificationMsg({ type: 'success', text: 'Regras de pontuação salvas!' });
      if (onRefreshContainer) onRefreshContainer();
    } catch(err: any) {
      setGamificationMsg({ type: 'error', text: 'Erro ao salvar: ' + err.message });
    } finally {
      setGamificationLoading(false);
    }
  };

  const handleRequestPlanChange = async () => {
    if (!container || !confirmationModal) return;
    setRequestLoading(true);
    
    try {
        const { data: superAdmins } = await supabase.from('profiles').select('id').eq('role', UserRole.SUPERADMIN);
        
        const typeLabel = confirmationModal.type === 'cancel' ? 'Cancelamento' : 'Mudança de Plano';
        const detail = confirmationModal.type === 'cancel' 
            ? `O gabinete ${container.name} solicitou cancelamento da assinatura.` 
            : `O gabinete ${container.name} deseja mudar para o plano ${confirmationModal.planName}.`;

        const notifications = superAdmins?.map(admin => ({
            user_id: admin.id,
            type: 'system_alert',
            title: `Solicitação: ${typeLabel}`,
            description: detail,
            related_id: container.id,
            read: false
        })) || [];

        if (notifications.length > 0) {
            await supabase.from('notifications').insert(notifications);
        }

        const requestData = {
            container_id: container.id,
            requested_by: currentUser.id,
            request_type: confirmationModal.type === 'cancel' ? 'cancel_subscription' : 'change_plan',
            target_plan: confirmationModal.targetPlan || null,
            status: 'pending'
        };
        
        const { error: reqError } = await supabase.from('plan_requests').insert([requestData]);
        if (reqError) {
            console.warn("Tabela plan_requests não encontrada ou erro de permissão (ignorado):", reqError.message);
        }

        setRequestSuccess(true);
        setTimeout(() => {
            setConfirmationModal(null);
            setRequestSuccess(false);
        }, 2000);

    } catch (err: any) {
        alert("Erro ao enviar solicitação: " + err.message);
    } finally {
        setRequestLoading(false);
    }
  };

  const handleAddSector = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSectorName.trim() || !container) return;
    
    // Check duplicates
    if (DEFAULT_TASK_SECTORS.includes(newSectorName.trim()) || customSectors.some(s => s.name === newSectorName.trim())) {
      alert("Este setor já existe.");
      return;
    }

    setSectorLoading(true);
    try {
      const { error } = await supabase.from('task_sectors').insert([{
        container_id: container.id,
        name: newSectorName.trim(),
        created_by: currentUser.id,
        is_active: true
      }]);
      
      if (error) throw error;
      setNewSectorName('');
      if (onUpdateSectors) onUpdateSectors();
    } catch (e: any) {
      alert("Erro ao adicionar setor: " + e.message);
    } finally {
      setSectorLoading(false);
    }
  };

  const toggleCustomSector = async (id: string, currentStatus: boolean) => {
    setSectorLoading(true);
    try {
      const { error } = await supabase.from('task_sectors').update({ is_active: !currentStatus }).eq('id', id);
      if (error) throw error;
      if (onUpdateSectors) onUpdateSectors();
    } catch (e: any) {
      alert("Erro ao alterar setor: " + e.message);
    } finally {
      setSectorLoading(false);
    }
  };

  const toggleDefaultSector = async (sectorName: string) => {
    if (!container) return;
    setSectorLoading(true);
    try {
      let newDisabled = [...disabledDefaults];
      if (newDisabled.includes(sectorName)) {
        // Restore
        newDisabled = newDisabled.filter(s => s !== sectorName);
      } else {
        // Disable
        newDisabled.push(sectorName);
      }

      const { error } = await supabase.from('containers').update({ disabled_sectors: newDisabled }).eq('id', container.id);
      if (error) throw error;
      if (onRefreshContainer) onRefreshContainer();
    } catch (e: any) {
      alert("Erro ao alterar setor padrão: " + e.message);
    } finally {
      setSectorLoading(false);
    }
  };

  const handleUpdatePermissions = async (userId: string, permissions: UserPermissions) => { /* Impl. no TeamList */ };
  const handleUpdateUser = async (userId: string, updates: Partial<User>) => { const { error } = await supabase.from('profiles').update(updates).eq('id', userId); if(!error) onUpdateUsers(); };

  return (
    <div className="max-w-6xl mx-auto animate-in fade-in duration-500 pb-20 relative">
      
      {/* ... (Existing modals for billing) ... */}
      {manageSubscriptionOpen && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-6 bg-navy-950/70 backdrop-blur-sm animate-in fade-in" onClick={() => setManageSubscriptionOpen(false)}>
           <div className="bg-white dark:bg-navy-900 w-full max-w-md rounded-[32px] shadow-2xl p-8 border border-neutral-200 dark:border-navy-800" onClick={e => e.stopPropagation()}>
              <div className="flex justify-between items-center mb-6">
                 <h3 className="text-xl font-black text-navy-950 dark:text-white">Gerenciar Assinatura</h3>
                 <button onClick={() => setManageSubscriptionOpen(false)} className="p-2 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-full"><X size={20} className="text-slate-400"/></button>
              </div>
              <div className="space-y-3">
                 <button 
                    onClick={() => {
                        setManageSubscriptionOpen(false);
                        const el = document.getElementById('plans-section');
                        if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }} 
                    className="w-full py-4 bg-navy-950 dark:bg-white text-white dark:text-navy-950 rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2 hover:scale-[1.02] transition-all"
                 >
                    <ArrowUpRight size={16}/> Alterar Plano
                 </button>
                 <button 
                    onClick={() => { setManageSubscriptionOpen(false); setConfirmationModal({ open: true, type: 'cancel' }); }} 
                    className="w-full py-4 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-2xl font-black text-xs uppercase tracking-widest border border-red-100 dark:border-red-900/30 hover:bg-red-100 transition-all"
                 >
                    Solicitar Cancelamento
                 </button>
              </div>
           </div>
        </div>
      )}

      {confirmationModal && (
        <div className="fixed inset-0 z-[160] flex items-center justify-center p-6 bg-navy-950/80 backdrop-blur-md animate-in fade-in" onClick={() => !requestLoading && setConfirmationModal(null)}>
           <div className="bg-white dark:bg-navy-900 w-full max-w-md rounded-[32px] shadow-2xl p-10 border border-neutral-200 dark:border-navy-800 text-center" onClick={e => e.stopPropagation()}>
              {requestSuccess ? (
                 <div className="animate-in zoom-in">
                    <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={40} /></div>
                    <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-2">Solicitação Enviada!</h3>
                    <p className="text-slate-500 font-medium text-sm">O suporte administrativo foi notificado e entrará em contato em breve.</p>
                 </div>
              ) : (
                 <>
                    <div className="w-20 h-20 bg-parliament-100 dark:bg-parliament-900/30 text-parliament-600 rounded-full flex items-center justify-center mx-auto mb-6"><Send size={32} /></div>
                    <h3 className="text-2xl font-black text-navy-950 dark:text-white mb-4">Enviar Solicitação?</h3>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-sm mb-8 leading-relaxed">
                       {confirmationModal.type === 'cancel' 
                          ? "Deseja enviar uma solicitação de cancelamento de assinatura ao suporte?" 
                          : `Deseja enviar uma solicitação de mudança para o plano ${confirmationModal.planName} ao suporte?`}
                    </p>
                    <div className="flex gap-3">
                       <button onClick={() => setConfirmationModal(null)} disabled={requestLoading} className="flex-1 py-3 text-slate-400 font-bold text-xs uppercase hover:bg-slate-50 dark:hover:bg-navy-800 rounded-xl transition-all">Cancelar</button>
                       <button onClick={handleRequestPlanChange} disabled={requestLoading} className="flex-1 bg-navy-950 dark:bg-white text-white dark:text-navy-950 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2">
                          {requestLoading ? <Loader2 className="animate-spin" size={14}/> : 'Sim, Enviar'}
                       </button>
                    </div>
                 </>
              )}
           </div>
        </div>
      )}

      {/* ... (Header Tabs) ... */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <h1 className="text-5xl font-black tracking-tighter mb-2 text-navy-950 dark:text-white">Ajustes</h1>
          <p className="text-neutral-500 dark:text-slate-400 font-medium">Configurações de equipe, faturamento e perfil pessoal.</p>
        </div>
        
        <div className="flex bg-neutral-100 dark:bg-navy-900 p-1.5 rounded-2xl border border-neutral-200 dark:border-navy-800 shadow-inner overflow-x-auto">
           {(isCabinetAdmin || isSuperAdmin) && (
             <button onClick={() => setActiveSubTab('team')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'team' ? 'bg-white dark:bg-navy-950 text-black dark:text-white shadow-lg' : 'text-neutral-400'}`}>
               <Users size={16} /> Equipe
             </button>
           )}
           {(isCabinetAdmin || isSuperAdmin) && (
             <button onClick={() => setActiveSubTab('workflow')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'workflow' ? 'bg-white dark:bg-navy-950 text-black dark:text-white shadow-lg' : 'text-neutral-400'}`}>
               <GitBranch size={16} /> Fluxo
             </button>
           )}
           {/* ... Other tabs ... */}
           {(isCabinetAdmin || isSuperAdmin) && (
             <button onClick={() => setActiveSubTab('billing')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'billing' ? 'bg-white dark:bg-navy-950 text-black dark:text-white shadow-lg' : 'text-neutral-400'}`}>
               <CreditCard size={16} /> Faturamento
             </button>
           )}
           {(isCabinetAdmin || isSuperAdmin) && (
             <button onClick={() => setActiveSubTab('gamification')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'gamification' ? 'bg-white dark:bg-navy-950 text-black dark:text-white shadow-lg' : 'text-neutral-400'}`}>
               <Trophy size={16} /> Gamificação
             </button>
           )}
           <button onClick={() => setActiveSubTab('profile')} className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shrink-0 ${activeSubTab === 'profile' ? 'bg-white dark:bg-navy-950 text-black dark:text-white shadow-lg' : 'text-neutral-400'}`}>
             <UserIcon size={16} /> Perfil
           </button>
        </div>
      </div>

      {activeSubTab === 'team' && (
        <>
          <TeamList
            users={users}
            currentUser={currentUser}
            sessionLogs={sessionLogs}
            onUpdatePermissions={handleUpdatePermissions}
            onUpdateUser={handleUpdateUser}
            onCreateUser={async () => {}}
            container={container}
          />
          {/* Assessores WhatsApp */}
          {container?.plan === 'betatester' && (
            <div className="mt-8 bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 p-8 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-black text-navy-950 dark:text-white flex items-center gap-2"><Phone size={20} className="text-emerald-600"/> Assessores WhatsApp</h3>
                  <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">Pessoas sem conta que interagem via WhatsApp.</p>
                </div>
              </div>
              <AddAssessorForm containerId={container!.id} currentUserId={currentUser.id} onDone={onUpdateAssessors} />
              {waAssessors.length === 0 ? (
                <p className="text-sm text-neutral-400 italic">Nenhum assessor WhatsApp cadastrado.</p>
              ) : (
                <div className="space-y-3">
                  {waAssessors.map(a => (
                    <AssessorRow key={a.id} assessor={a} sendingId={sendingAssessorId} sendResult={assessorSendResult} onSendTasks={handleSendAssessorTasks} onUpdate={onUpdateAssessors} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {activeSubTab === 'workflow' && (
        <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4">
           {/* Workflow code remains unchanged */}
           <div className="bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 p-8 shadow-sm">
              <div className="flex justify-between items-center mb-6">
                 <div>
                    <h3 className="text-xl font-black text-navy-950 dark:text-white flex items-center gap-2"><GitBranch size={20} className="text-parliament-600"/> Setores de Tarefas</h3>
                    <p className="text-slate-500 dark:text-slate-400 text-sm font-medium mt-1">Personalize os setores disponíveis para categorização.</p>
                 </div>
                 <button onClick={() => setShowDeletedSectors(!showDeletedSectors)} className={`text-[9px] font-bold uppercase tracking-widest px-3 py-2 rounded-lg transition-all ${showDeletedSectors ? 'bg-red-50 text-red-600' : 'text-slate-400 hover:bg-slate-50'}`}>
                    {showDeletedSectors ? 'Ocultar Desativados' : 'Ver Desativados'}
                 </button>
              </div>
              
              {/* Default Sectors Section */}
              <div className="space-y-4 mb-8 pb-8 border-b border-neutral-100 dark:border-navy-800">
                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Setores Padrão (Sistema)</p>
                 <div className="flex flex-wrap gap-2">
                    {DEFAULT_TASK_SECTORS.map(s => {
                       const isDisabled = disabledDefaults.includes(s);
                       if (isDisabled && !showDeletedSectors) return null;
                       return (
                          <div key={s} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${isDisabled ? 'bg-red-50 text-red-400 border-red-100 line-through dark:bg-red-900/20 dark:border-red-900/30' : 'bg-slate-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-navy-700'}`}>
                             {s}
                             <button onClick={() => toggleDefaultSector(s)} disabled={sectorLoading} className={`transition-colors ${isDisabled ? 'text-emerald-500 hover:text-emerald-700' : 'text-slate-400 hover:text-red-500'}`}>
                                {isDisabled ? <RotateCcw size={12}/> : <Trash2 size={12}/>}
                             </button>
                          </div>
                       );
                    })}
                 </div>
              </div>

              {/* Custom Sectors Section */}
              <div className="space-y-4">
                 <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Setores Personalizados</p>
                 <form onSubmit={handleAddSector} className="flex gap-2">
                    <input 
                      value={newSectorName}
                      onChange={e => setNewSectorName(e.target.value)}
                      placeholder="Novo Setor..." 
                      className="flex-1 bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 text-sm font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" 
                    />
                    <button disabled={sectorLoading || !newSectorName} type="submit" className="bg-navy-950 dark:bg-parliament-600 text-white p-3 rounded-xl shadow-lg disabled:opacity-50">
                       {sectorLoading ? <Loader2 className="animate-spin" size={20}/> : <Plus size={20}/>}
                    </button>
                 </form>

                 <div className="flex flex-wrap gap-2 mt-4">
                    {activeCustom.map(s => (
                       <span key={s.id} className="flex items-center gap-2 px-3 py-1.5 bg-parliament-50 dark:bg-parliament-900/20 text-parliament-700 dark:text-parliament-300 rounded-lg text-xs font-bold border border-parliament-100 dark:border-parliament-800">
                          {s.name}
                          <button onClick={() => toggleCustomSector(s.id, true)} disabled={sectorLoading} className="text-parliament-400 hover:text-red-500 transition-colors"><Trash2 size={12}/></button>
                       </span>
                    ))}
                    {showDeletedSectors && inactiveCustom.map(s => (
                       <span key={s.id} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-400 rounded-lg text-xs font-bold border border-red-100 line-through">
                          {s.name}
                          <button onClick={() => toggleCustomSector(s.id, false)} disabled={sectorLoading} className="text-red-400 hover:text-emerald-500 transition-colors"><RotateCcw size={12}/></button>
                       </span>
                    ))}
                    {activeCustom.length === 0 && (!showDeletedSectors || inactiveCustom.length === 0) && (
                       <p className="text-xs text-slate-400 italic">Nenhum setor personalizado.</p>
                    )}
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* ... (Existing billing/gamification tabs) ... */}
      {activeSubTab === 'billing' && (
        <div className="animate-in fade-in slide-in-from-bottom-4 space-y-10">
            {/* ... Billing content remains unchanged ... */}
            {/* Header do Plano Atual */}
            <div className="p-10 bg-gradient-to-r from-navy-900 to-navy-950 dark:from-black dark:to-navy-950 rounded-[40px] shadow-2xl relative overflow-hidden text-white flex flex-col md:flex-row justify-between items-center gap-8">
                <div className="absolute top-0 right-0 w-64 h-64 bg-parliament-500 rounded-full blur-[100px] opacity-20 pointer-events-none"></div>
                <div className="relative z-10">
                   <div className="flex items-center gap-3 mb-2">
                      <p className="text-[10px] font-black uppercase tracking-widest opacity-60">Plano Ativo</p>
                      <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest border border-white/20 ${container?.status === 'active' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
                          {container?.status === 'active' ? 'Regular' : 'Pendente'}
                      </span>
                   </div>
                   <h3 className="text-5xl font-black tracking-tighter mb-4">{currentPlanDetails.name}</h3>
                   <p className="text-sm font-medium opacity-70 max-w-md">
                      Seu gabinete tem acesso a recursos exclusivos. Gerencie suas faturas ou altere seu plano a qualquer momento.
                   </p>
                </div>
                <div className="relative z-10 text-right">
                   <p className="text-4xl font-black">{container?.billing_cycle === 'monthly' ? currentPlanDetails.priceM : currentPlanDetails.priceY}<span className="text-sm font-medium opacity-50">/mês</span></p>
                   <button onClick={() => setManageSubscriptionOpen(true)} className="mt-4 px-6 py-3 bg-white text-navy-950 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-100 transition-all shadow-lg">
                      Gerenciar Assinatura
                   </button>
                </div>
            </div>

            {/* Cards de Planos */}
            <div id="plans-section" className="grid grid-cols-1 md:grid-cols-3 gap-6">
               {plans.map(plan => {
                  const isActive = currentPlan === plan.id;
                  return (
                    <div key={plan.id} className={`relative p-8 rounded-[32px] border-2 transition-all flex flex-col h-full ${isActive ? 'bg-white dark:bg-navy-900 border-parliament-600 dark:border-parliament-500 shadow-xl scale-[1.02] z-10' : 'bg-white dark:bg-navy-900 border-neutral-100 dark:border-navy-800 hover:border-neutral-200 dark:hover:border-navy-700 opacity-80 hover:opacity-100'}`}>
                       {isActive && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-parliament-600 text-white px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest shadow-md">
                             Selecionado
                          </div>
                       )}
                       <div className="mb-6">
                          <h4 className="text-xl font-black text-navy-950 dark:text-white mb-2">{plan.name}</h4>
                          <p className="text-2xl font-black text-parliament-600">{container?.billing_cycle === 'yearly' ? plan.priceY : plan.priceM}<span className="text-xs text-slate-400 font-bold">/mês</span></p>
                       </div>
                       
                       <div className="space-y-4 mb-8 flex-1">
                          <div className="flex items-center gap-3">
                             <div className="p-2 bg-neutral-100 dark:bg-navy-800 rounded-lg text-slate-600 dark:text-slate-300"><Users size={16}/></div>
                             <p className="text-xs font-bold text-slate-600 dark:text-slate-300">{plan.contactLabel}</p>
                          </div>
                          <div className="flex items-center gap-3">
                             <div className="p-2 bg-neutral-100 dark:bg-navy-800 rounded-lg text-slate-600 dark:text-slate-300"><HardDrive size={16}/></div>
                             <p className="text-xs font-bold text-slate-600 dark:text-slate-300">{plan.storageLabel}</p>
                          </div>
                       </div>

                       <button 
                          disabled={isActive} 
                          onClick={() => setConfirmationModal({ open: true, type: 'change', targetPlan: plan.id, planName: plan.name })}
                          className={`w-full py-3 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isActive ? 'bg-neutral-100 dark:bg-navy-800 text-slate-400 cursor-default' : 'bg-navy-950 dark:bg-white text-white dark:text-navy-950 hover:opacity-90 shadow-lg'}`}
                       >
                          {isActive ? 'Plano Atual' : 'Alterar Plano'}
                       </button>
                    </div>
                  );
               })}
            </div>
        </div>
      )}

      {activeSubTab === 'gamification' && (
        <div className="max-w-2xl animate-in fade-in slide-in-from-bottom-4">
           {/* Gamification content remains same (omitted for brevity) */}
           <div className="bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 p-8 shadow-sm">
              <h3 className="text-xl font-black text-navy-950 dark:text-white mb-6 flex items-center gap-2"><Trophy size={20} className="text-parliament-600"/> Regras de Pontuação</h3>
              
              {gamificationMsg && (
                <div className={`p-4 rounded-2xl mb-6 text-xs font-bold flex items-center gap-2 ${gamificationMsg.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                   {gamificationMsg.type === 'success' ? <CheckCircle2 size={16}/> : <AlertTriangle size={16}/>}
                   {gamificationMsg.text}
                </div>
              )}

              <form onSubmit={handleUpdateGamification} className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Pontos por Contato Cadastrado</label>
                    <input type="number" value={pointsConfig.contact_points} onChange={e => setPointsConfig({...pointsConfig, contact_points: Number(e.target.value)})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Pontos por Atendimento Iniciado</label>
                    <input type="number" value={pointsConfig.attendance_points} onChange={e => setPointsConfig({...pointsConfig, attendance_points: Number(e.target.value)})} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" />
                 </div>
                 <button disabled={gamificationLoading} type="submit" className="w-full py-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2">
                    {gamificationLoading ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Salvar Regras
                 </button>
              </form>
           </div>
        </div>
      )}

      {activeSubTab === 'profile' && (
        <div className="max-w-xl animate-in fade-in slide-in-from-bottom-4">
           <div className="bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 p-8 shadow-sm">
              <h3 className="text-xl font-black text-navy-950 dark:text-white mb-6 flex items-center gap-2"><UserIcon size={20} className="text-parliament-600"/> Meu Perfil</h3>
              
              {nameMsg && (
                <div className={`p-4 rounded-2xl mb-6 text-xs font-bold flex items-center gap-2 ${nameMsg.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                   {nameMsg.type === 'success' ? <CheckCircle2 size={16}/> : <AlertTriangle size={16}/>}
                   {nameMsg.text}
                </div>
              )}

              <form onSubmit={handleUpdateName} className="space-y-6">
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Nome de Exibição</label>
                    <input value={newName} onChange={e => setNewName(e.target.value)} className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" />
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Telefone / WhatsApp</label>
                    <input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="5511999999999" className="w-full bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 font-bold dark:text-white outline-none focus:ring-2 focus:ring-parliament-600" />
                    <p className="text-[9px] text-slate-400 ml-1">Usado para lembretes de tarefas via WhatsApp</p>
                 </div>
                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">E-mail (Login)</label>
                    <input disabled value={currentUser.email} className="w-full bg-slate-100 dark:bg-navy-800 border border-transparent rounded-xl px-4 py-3 font-bold text-slate-500 cursor-not-allowed" />
                 </div>
                 <button disabled={nameLoading} type="submit" className="w-full py-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2">
                    {nameLoading ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Atualizar Perfil
                 </button>
              </form>

              <div className="mt-8 pt-8 border-t border-neutral-100 dark:border-navy-800">
                 <button 
                   onClick={onOpenSupport}
                   className="w-full py-4 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-300 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all flex items-center justify-center gap-2"
                 >
                    <MessageSquare size={16}/> Enviar Sugestão / Suporte
                 </button>
              </div>
           </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettings;